import "./App.scss";
import React, { useEffect, useMemo, useState } from "react";
import ForgotPassword from "./components/Auth/ForgotPassword";
import Login from "./components/Auth/Login";
import { Routes, Route, useLocation } from "react-router-dom";
import SignUp from "./components/Auth/SignUp";
import AuthenticatedRoute from "./AuthenticatedRoute";
import { NotifContextProvider } from "./components/dashboard/components/notifications/context/notifContext";
import { AuthProvider, useAuth } from "./authentication/authHelpers";
import PublicRoute from "./PublicRoute";
import { API } from "./helpers/API/amplifyApi";
import { Auth } from "./authentication/amplifyAuth";
import moment from "moment-timezone";
import { Provider } from "react-redux";
import { useDispatch } from "react-redux";
import { store } from "./store/store";
import accessToken from "./store/actions/accessToken";
import { hotCredentials, users as usersReducer } from "./store/actions";
import { gapi } from "gapi-script";
import Axios from "axios";
import "antd/dist/reset.css";
import { ReportProvider } from "./components/Reports/generate/reportContext/reportHelpers";
import OtpLogin from "./components/Auth/OtpLogin";
import AuthInterface from "./components/Auth/AuthInterface";
import dayjs from "dayjs";
import dayjsTZ from "./utils/dayjs";
import { compareToken } from "./authentication/handle2FaToken";
import { useSelector } from "react-redux";
import { message } from "antd";
import ReactGA from "react-ga4";
import PathTracker from "./authentication/PathTracker";
import PostHogIdentity from "./analytics/PostHogIdentity";
import TwoFactorAuth from "./components/Auth/TwoFactorAuth";

const GOOGLE_ANALYTICS_TAG_ID = "G-Z2G5N2REM1";

function App() {
  moment.tz.setDefault("Europe/Tirane");
  const dispatch = useDispatch();
  const auth = useAuth();
  const location = useLocation();

  const { users, isDarkMode } = useSelector((state) => state.usersList);

  //* state to get hotCreds
  const [hotCreds, setHotCreds] = useState([]);
  const [authData, setAuthData] = useState({});

  //! gets hotCreds from database
  //UseEffect to reset the window location state when the page gets reloaded
  useEffect(() => {
    // Only fetch after Cognito Identity credentials exist (IAM authorizer).
    Auth.currentCredentials()
      .then(() => API.get("hotCredentials", "/hotCredentials"))
      .then((res) => {
        dispatch(hotCredentials(res));
        setHotCreds(res);
      })
      .catch((err) => {
        console.log("err: ", err);
      });
    window.addEventListener("beforeunload", () => {
      window.history.replaceState({}, "");
    });

    let values = [
      "draggableSetingsPozicionet",
      "draggableSetingsFitimet",
      "draggableSetingsZbritjet",
      "draggableSetingsInfoRrethKompanis",
      "draggableOrderDepartments",
      "draggableProfili",
      "draggableOrderSuperVisor",
      "draggableOrderUser",
      "draggableOrderAdmin",
    ];

    values.forEach((item) => {
      let value = localStorage.getItem(item);
      if (value) {
        value = JSON.parse(value);
        if (value[0].hasOwnProperty("id")) {
          localStorage.removeItem(item);
        }
      }
    });

    ReactGA.initialize(GOOGLE_ANALYTICS_TAG_ID);
  }, []);

  //! initializes accesToken
  const auth2 = () => {
    // init() rejects whenever the page origin is not an authorized JavaScript
    // origin of the OAuth client (localhost, typically) or the legacy auth2
    // iframe cannot load. gapi rethrows an unhandled rejection from its own
    // setTimeout, and since apis.google.com is cross-origin the browser strips
    // the details: the app sees a bare "Script error." with no stack. Adopting
    // the thenable here marks it handled, so a failed init stays a log line.
    Promise.resolve(
      gapi.client?.init({
        clientId: hotCreds?.find(({ credentialId }) => credentialId === "3")
          ?.credentialValue,
        scope:
          "https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/drive.metadata.readonly https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/gmail.insert https://www.googleapis.com/auth/gmail.compose https://www.googleapis.com/auth/gmail.readonly https://mail.google.com/",
      })
    ).catch((err) => console.log("Error initializing gapi client: ", err));

    gapi.client?.setApiKey(
      hotCreds?.find(({ credentialId }) => credentialId === "2")
        ?.credentialValue
    );
  };

  //! gets accessToken
  useEffect(() => {
    if (hotCreds.length) {
      const toSend = {
        clientId: hotCreds?.find(({ credentialId }) => credentialId === "3")
          ?.credentialValue,
        client_secret: hotCreds?.find(
          ({ credentialId }) => credentialId === "4"
        )?.credentialValue,
        refresh_token: hotCreds?.find(
          ({ credentialId }) => credentialId === "1"
        )?.credentialValue,
        grant_type: "refresh_token",
      };
      Axios.post("https://oauth2.googleapis.com/token", { ...toSend })
        .then(({ data }) => {
          dispatch(accessToken(data.access_token));
        })
        .catch((err) => {
          console.log("Error loading google token: ", err);
        });
      gapi.load("client", () => {
        auth2();
        gapi.client.load("drive", "v3", function () {});
      });
    }

    //interval to get the new access token every 1 hour
    const accessTokenIterval = setInterval(() => {
      if (hotCreds) {
        const toSend = {
          clientId: hotCreds?.find(({ credentialId }) => credentialId === "3")
            ?.credentialValue,
          client_secret: hotCreds?.find(
            ({ credentialId }) => credentialId === "4"
          )?.credentialValue,
          refresh_token: hotCreds?.find(
            ({ credentialId }) => credentialId === "1"
          )?.credentialValue,
          grant_type: "refresh_token",
        };
        Axios.post("https://oauth2.googleapis.com/token", { ...toSend }).then(
          ({ data }) => {
            dispatch(accessToken(data.access_token));
          }
        );
        gapi.load("client", () => {
          auth2();
          gapi.client.load("drive", "v3", function () {});
        });
      }
    }, [3500000]);

    return () => clearInterval(accessTokenIterval);
  }, [hotCreds]);

  const getAuthData = async () => {
    await API.get("users", "/users")
      .then((res) => setAuthData(res?.auth2Fa))
      .catch((err) => setAuthData({}));
  };

  useEffect(() => {
    if (location.pathname === "/login") {
      getAuthData();
    }
  }, [auth]);

  const renderLogin = useMemo(() => {
    const { codeSent, active2FaSessions } = authData || {};

    const session = (active2FaSessions || [])?.find((el) =>
      compareToken("2FaSessionToken", el?.sessionId)
    );

    if (!!auth?.auth2Fa?.codeSent && !session?.isAuthenticated) {
      return (
        <AuthInterface
          title={"Shkruani kodin e ardhur në emailin tuaj!"}
          children={<OtpLogin />}
        />
      );
    } else if (
      (!codeSent && !session?.isAuthenticated) ||
      !auth?.hasOwnProperty("auth2Fa")
    ) {
      return (
        <AuthInterface title={"Futu në adresën tënde!"} children={<Login />} />
      );
    }
  }, [authData, auth]);
  // const renderLogin = useMemo(() => {
  //   const { codeSent, isEnabled } = auth?.auth2Fa || {};

  //   // const session = (active2FaSessions || [])?.find((el) =>
  //   //   compareToken("2FaSessionToken", el?.sessionId)
  //   // );

  //   return codeSent ? (
  //     <AuthInterface
  //       title={"Shkruani kodin e ardhur në emailin tuaj!"}
  //       children={<OtpLogin />}
  //     />
  //   ) : (
  //     <AuthInterface title={"Futu në adresën tënde!"} children={<Login />} />
  //   );
  // }, [auth]);

  //region RETURN
  return (
    <div
      className={`App ${isDarkMode ? "dark" : ""}`}
      style={{ width: "100% ", height: "100%" }}
    >
      <ReportProvider>
        <Provider store={store}>
          <PathTracker />
          <PostHogIdentity />
          <Routes>
            <Route exact path="/" element={<PublicRoute />}>
              <Route path="/2fa" element={<TwoFactorAuth />} />
              <Route path="/login" element={renderLogin} />
              <Route path="/rregjistrohu" element={<SignUp />} />
              <Route
                path="/harruatkodin"
                element={
                  <AuthInterface
                    title={"KE HARRUAR FJALËKALIMIN ?"}
                    children={<ForgotPassword />}
                  />
                }
              />
            </Route>
          </Routes>
          <NotifContextProvider>
            <AuthenticatedRoute />
          </NotifContextProvider>
        </Provider>
      </ReportProvider>
    </div>
  );
}

export default App;
